import { Injectable } from '@angular/core';

/**
 * POS Sound Service
 * Synthetische Töne für Kassensystem-Aktionen via Web Audio API.
 * Keine externen Audiodateien nötig.
 *
 * Usage:
 *   constructor(private sound: PosSoundService) {}
 *
 *   this.sound.click()       — Minimaler Klick (Artikel antippen)
 *   this.sound.addToCart()   — In den Warenkorb
 *   this.sound.remove()      — Artikel gelöscht
 *   this.sound.checkout()    — Bezahlen starten
 *
 *   this.sound.setVolume(0.5)
 *   this.sound.toggleMute()
 *
 * Lautstärke und Mute-Status werden in localStorage gespeichert und
 * überleben einen Reload. Alle Audio-Operationen sind so gekapselt, dass
 * sie auf Plattformen ohne Web Audio (oder bei stummgeschaltetem Ton)
 * niemals einen Fehler werfen.
 */
@Injectable({ providedIn: 'root' })
export class PosSoundService {
  private static readonly VOLUME_KEY = 'pos-sound.volume';
  private static readonly MUTED_KEY = 'pos-sound.muted';

  private _volume = 0.4;
  private _muted = false;
  private _ctx: AudioContext | null = null;

  constructor() {
    this.restoreSettings();
    this.installAudioUnlock();
  }

  // --- Public API -----------------------------------------------------------

  /** Minimaler Klick — kurzer perkussiver Tick */
  click(): void {
    this.noise(0.008, 0.45, 2500, 'bandpass');
  }

  /** In den Warenkorb — aufsteigender Doppelton */
  addToCart(): void {
    this.sequence([
      { freq: 600, duration: 0.08, volume: 0.35 },
      { freq: 900, duration: 0.12, volume: 0.40 },
    ]);
  }

  /** Gelöscht — absteigender Ton */
  remove(): void {
    this.sequence([
      { freq: 700, duration: 0.08, volume: 0.30 },
      { freq: 400, duration: 0.15, volume: 0.25 },
    ]);
  }

  /** Bezahlen starten — aufsteigender Dreiklang (C-E-G) */
  checkout(): void {
    this.sequence([
      { freq: 523, duration: 0.10, volume: 0.35 },
      { freq: 659, duration: 0.10, volume: 0.40 },
      { freq: 784, duration: 0.18, volume: 0.45 },
    ]);
  }

  // --- Volume Control -------------------------------------------------------

  setVolume(v: number): void {
    this._volume = Math.max(0, Math.min(1, v));
    this.persist(PosSoundService.VOLUME_KEY, String(this._volume));
  }

  getVolume(): number {
    return this._volume;
  }

  mute(): void {
    this._muted = true;
    this.persist(PosSoundService.MUTED_KEY, 'true');
  }

  unmute(): void {
    this._muted = false;
    this.persist(PosSoundService.MUTED_KEY, 'false');
  }

  toggleMute(): boolean {
    this._muted = !this._muted;
    this.persist(PosSoundService.MUTED_KEY, String(this._muted));
    return this._muted;
  }

  isMuted(): boolean {
    return this._muted;
  }

  // --- Internals ------------------------------------------------------------

  /**
   * Browsers only let an AudioContext start/resume from within a user gesture.
   * Sounds that fire after an awaited backend call (addToCart/remove) run
   * outside that gesture window, so if the context is still suspended — e.g.
   * right after a page reload or once the browser auto-suspended it while the
   * screen was idle — they would silently play nothing. Resuming on the first
   * pointer/touch/key event guarantees the context is running by the time any
   * cue plays.
   */
  private installAudioUnlock(): void {
    if (typeof document === 'undefined') {
      return;
    }
    const unlock = () => {
      try {
        const ctx = this.getContext();
        if (ctx && ctx.state === 'suspended') {
          ctx.resume();
        }
      } catch {
        // Web Audio may be unavailable — ignore.
      }
    };
    const options: AddEventListenerOptions = { capture: true, passive: true };
    ['pointerdown', 'touchstart', 'keydown'].forEach((event) =>
      document.addEventListener(event, unlock, options)
    );
  }

  private getContext(): AudioContext | null {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) {
      return null;
    }
    if (!this._ctx || this._ctx.state === 'closed') {
      this._ctx = new Ctx();
    }
    if (this._ctx.state === 'suspended') {
      this._ctx.resume();
    }
    return this._ctx;
  }

  private effectiveVolume(): number {
    return this._muted ? 0 : this._volume;
  }

  private tone(
    freq: number,
    duration: number,
    volume: number,
    type: OscillatorType = 'sine',
    fadeOut = true
  ): void {
    if (this.effectiveVolume() === 0) {
      return;
    }
    try {
      const ctx = this.getContext();
      if (!ctx) {
        return;
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.frequency.value = freq;
      osc.type = type;
      gain.gain.setValueAtTime(volume * this.effectiveVolume(), ctx.currentTime);

      if (fadeOut) {
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      }

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch {
      // Audio darf niemals einen Fehler in den POS-Flow werfen.
    }
  }

  private sequence(
    notes: { freq: number; duration: number; volume?: number; type?: OscillatorType; fadeOut?: boolean }[]
  ): void {
    if (this.effectiveVolume() === 0) {
      return;
    }
    try {
      const ctx = this.getContext();
      if (!ctx) {
        return;
      }
      let offset = 0;

      for (const note of notes) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.frequency.value = note.freq;
        osc.type = note.type ?? 'sine';

        const vol = (note.volume ?? 0.4) * this.effectiveVolume();
        gain.gain.setValueAtTime(vol, ctx.currentTime + offset);

        if (note.fadeOut !== false) {
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + note.duration);
        }

        osc.start(ctx.currentTime + offset);
        osc.stop(ctx.currentTime + offset + note.duration);

        offset += note.duration;
      }
    } catch {
      // Audio darf niemals einen Fehler in den POS-Flow werfen.
    }
  }

  private noise(duration: number, volume: number, filterFreq = 4000, filterType: BiquadFilterType = 'bandpass'): void {
    if (this.effectiveVolume() === 0) {
      return;
    }
    try {
      const ctx = this.getContext();
      if (!ctx) {
        return;
      }
      const bufferSize = ctx.sampleRate * duration;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);

      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 8);
      }

      const source = ctx.createBufferSource();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();

      source.buffer = buffer;
      filter.type = filterType;
      filter.frequency.value = filterFreq;

      source.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);

      gain.gain.setValueAtTime(volume * this.effectiveVolume(), ctx.currentTime);
      source.start();
    } catch {
      // Audio darf niemals einen Fehler in den POS-Flow werfen.
    }
  }

  // --- Persistence ----------------------------------------------------------

  private restoreSettings(): void {
    try {
      const storedVolume = localStorage.getItem(PosSoundService.VOLUME_KEY);
      if (storedVolume !== null) {
        const parsed = parseFloat(storedVolume);
        if (!isNaN(parsed)) {
          this._volume = Math.max(0, Math.min(1, parsed));
        }
      }
      this._muted = localStorage.getItem(PosSoundService.MUTED_KEY) === 'true';
    } catch {
      // localStorage kann fehlen/blockiert sein — Defaults sind in Ordnung.
    }
  }

  private persist(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Persistenz ist optional; Fehler dürfen den Flow nicht stören.
    }
  }
}
